import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, ShieldOff, KeyRound, Loader2, ChevronRight, Users, Search, AlertTriangle,
  SlidersHorizontal, Power, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TooltipButton } from "@/components/common/Tooltips";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const fecha = (iso) => {
  if (!iso) return "Nunca";
  try {
    return new Date(iso).toLocaleString("es-MX",
      { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso).slice(0, 16); }
};

const Badge2FA = ({ on, exigible, id }) => {
  if (!exigible) {
    return <span data-testid={`mfa2-badge-${id}`} className="text-xs text-slate-400">Sin 2FA (rol)</span>;
  }
  return (
    <span data-testid={`mfa2-badge-${id}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
        on ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
      {on ? <ShieldCheck className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />}
      {on ? "Activo" : "Apagado"}
    </span>
  );
};

export const Panel2FAUsuarios = () => {
  const [abierto, setAbierto] = useState(false);
  const [data, setData] = useState({ items: [], resumen: {} });
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [q, setQ] = useState("");
  const [soloExigibles, setSoloExigibles] = useState(true);
  const [soloApagados, setSoloApagados] = useState(false);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(null);
  const [masivo, setMasivo] = useState(null);   // vista previa de la activación masiva
  const [reset, setReset] = useState(null);     // usuario a resetear
  // Configuración del 2FA (iter127): interruptor general + interruptores por rol.
  const [confRol, setConfRol] = useState(null);   // { rol, label, impacto, accion }
  const [confGate, setConfGate] = useState(false); // confirmación de apagado general

  const load = useCallback(async () => {
    setCargando(true);
    try {
      const [u, c] = await Promise.all([
        api.get("/admin/mfa/usuarios"),
        api.get("/admin/mfa/config"),
      ]);
      setData(u.data || { items: [], resumen: {} });
      setConfig(c.data || null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { if (abierto) load(); }, [abierto, load]);

  // ── Interruptores de configuración ──────────────────────────────────────
  const patchConfig = async (payload, mensaje) => {
    const { data: r } = await api.patch("/admin/mfa/config", payload);
    if (mensaje) toast.success(mensaje);
    await load();
    return r;
  };

  const toggleGate = async (v) => {
    if (!v) { setConfGate(true); return; }   // apagar el 2FA de toda la plataforma: confirmar
    setBusy("gate");
    try {
      await patchConfig({ gate_activo: true },
        "2FA encendido para toda la plataforma. Aplica desde el siguiente inicio de sesión.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const confirmarApagadoGate = async () => {
    setBusy("gate");
    try {
      await patchConfig({ gate_activo: false },
        "2FA APAGADO para toda la plataforma. Nadie recibirá código al entrar.");
      setConfGate(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const toggleAuto = async (rol, v) => {
    setBusy(`auto-${rol}`);
    try {
      await patchConfig({ roles: { [rol]: { auto_activar: v } } },
        v ? "Los usuarios nuevos de ese rol nacerán con 2FA activo."
          : "Los usuarios nuevos de ese rol nacerán sin 2FA.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  // Apagar «pide código» avisa a cuántos afecta; encenderlo ofrece activarlo a los existentes.
  const toggleReta = async (rol, label, v) => {
    setBusy(`reta-${rol}`);
    try {
      const { data: imp } = await api.get(`/admin/mfa/rol/${rol}/impacto`);
      if (!v) {
        setConfRol({ rol, label, impacto: imp, accion: "apagar" });
      } else {
        await patchConfig({ roles: { [rol]: { reta_login: true } } },
          `«Pide código al entrar» encendido para ${label}.`);
        if (imp.sin_2fa > 0) setConfRol({ rol, label, impacto: imp, accion: "ofrecer" });
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const confirmarApagadoRol = async () => {
    setBusy("rol");
    try {
      await patchConfig({ roles: { [confRol.rol]: { reta_login: false } } },
        `«Pide código al entrar» apagado para ${confRol.label}.`);
      setConfRol(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const confirmarActivarRol = async () => {
    setBusy("rol");
    try {
      const { data: r } = await api.post("/admin/mfa/activar-todos",
        { rol: confRol.rol, confirmar: true });
      toast.success(`2FA activado en ${r.activados} usuario(s) de ${confRol.label}.`);
      setConfRol(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const filas = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data.items || []).filter((r) => {
      if (soloExigibles && !r.mfa_exigible_por_rol) return false;
      if (soloApagados && r.mfa_activo) return false;
      if (!t) return true;
      return `${r.name} ${r.email} ${r.entidad} ${r.role}`.toLowerCase().includes(t);
    });
  }, [data.items, q, soloExigibles, soloApagados]);

  const seleccionados = Object.keys(sel).filter((k) => sel[k]);
  const seleccionablesVisibles = filas.filter((r) => r.mfa_exigible_por_rol && !r.mfa_activo);

  const toggleTodosVisibles = (v) => {
    const next = { ...sel };
    seleccionablesVisibles.forEach((r) => { next[r.id] = v; });
    setSel(next);
  };

  const activarLote = async () => {
    setBusy("lote");
    try {
      const { data: r } = await api.post("/admin/mfa/activar-lote", { user_ids: seleccionados });
      toast.success(`2FA activado en ${r.activados} usuario(s).${r.omitidos ? ` ${r.omitidos} omitido(s).` : ""}`);
      setSel({});
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const abrirMasivo = async () => {
    setBusy("previa");
    try {
      const { data: r } = await api.get("/admin/mfa/objetivo-masivo");
      setMasivo(r);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const confirmarMasivo = async () => {
    setBusy("masivo");
    try {
      const { data: r } = await api.post("/admin/mfa/activar-todos", { confirmar: true });
      toast.success(`2FA activado en ${r.activados} usuario(s). Ya tenían 2FA: ${r.ya_tenian_2fa}.`);
      setMasivo(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const toggleUno = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/admin/users/${row.id}/mfa/${row.mfa_activo ? "deactivate" : "activate"}`);
      toast.success(`2FA ${row.mfa_activo ? "desactivado" : "activado"} para ${row.email}.`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const confirmarReset = async () => {
    setBusy("reset");
    try {
      await api.post(`/admin/users/${reset.id}/mfa/reset`);
      toast.success(`2FA reseteado. ${reset.email} vuelve a entrar con solo correo y contraseña.`);
      setReset(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const r = data.resumen || {};

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="panel-2fa">
      <button type="button" onClick={() => setAbierto((v) => !v)}
        data-testid="panel-2fa-toggle"
        className="flex w-full items-center justify-between p-5 text-left">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </span>
          <div>
            <p className="font-heading text-sm font-semibold text-slate-900">
              Gestión de 2FA (segundo factor por correo)
              {r.exigibles_sin_2fa ? (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"
                  data-testid="panel-2fa-pendientes">
                  {r.exigibles_sin_2fa} sin activar
                </span>
              ) : null}
            </p>
            <p className="text-xs text-slate-500">
              Todos los usuarios de la plataforma con su estado de 2FA y último acceso.
              Activación individual, en lote o masiva.
            </p>
          </div>
        </div>
        <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${abierto ? "rotate-90" : ""}`} />
      </button>

      {abierto && (
        <div className="border-t border-slate-100 p-5" data-testid="panel-2fa-body">
          {cargando ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando usuarios…
            </div>
          ) : (
            <>
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Usuarios totales", r.usuarios_totales, "usuarios-totales"],
                  ["Con 2FA activo", r.con_2fa_activo, "con-2fa"],
                  ["Exigibles por rol", r.exigibles_por_rol, "exigibles"],
                  ["Exigibles sin 2FA", r.exigibles_sin_2fa, "sin-2fa"],
                ].map(([lbl, val, tid]) => (
                  <div key={tid} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">{lbl}</p>
                    <p className="font-heading text-xl font-semibold text-slate-900"
                      data-testid={`panel-2fa-kpi-${tid}`}>{val ?? 0}</p>
                  </div>
                ))}
              </div>

              {config && (
                <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4"
                  data-testid="panel-2fa-config">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <SlidersHorizontal className="h-4 w-4 text-primary" />
                      <p className="font-heading text-sm font-semibold text-slate-900">
                        Configuración del 2FA
                      </p>
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
                        {config.fuente_de_configuracion}
                      </span>
                    </div>
                    {config.updated_at && (
                      <p className="text-[11px] text-slate-500" data-testid="conf-ultimo-cambio">
                        Último cambio: {fecha(config.updated_at)} · {config.updated_by}
                      </p>
                    )}
                  </div>

                  {/* Interruptor general de toda la plataforma */}
                  <div className={`mb-4 flex items-center justify-between rounded-md border px-4 py-3 ${
                    config.gate_activo ? "border-emerald-300 bg-emerald-50"
                                       : "border-red-300 bg-red-50"}`}
                    data-testid="conf-gate-fila">
                    <div className="flex items-center gap-3">
                      <Power className={`h-5 w-5 ${config.gate_activo ? "text-emerald-600" : "text-red-600"}`} />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          2FA de toda la plataforma:{" "}
                          <span data-testid="conf-gate-estado">
                            {config.gate_activo ? "ENCENDIDO" : "APAGADO"}
                          </span>
                        </p>
                        <p className="text-xs text-slate-600">
                          {config.gate_activo
                            ? "Los roles con «pide código al entrar» encendido reciben el segundo factor."
                            : "Nadie recibe código al entrar, sin importar los interruptores de cada rol."}
                        </p>
                      </div>
                    </div>
                    <Switch data-testid="conf-gate-switch" checked={!!config.gate_activo}
                      disabled={busy === "gate"} onCheckedChange={toggleGate} />
                  </div>

                  <p className="mb-2 text-xs text-slate-600">
                    {config.regla_segundo_login} Los roles salen del catálogo real del sistema
                    (<b data-testid="conf-total-roles">{(config.roles || []).length}</b> roles):
                    si mañana hay un rol nuevo, aparece solo.
                  </p>

                  <div className="max-h-[340px] overflow-auto rounded-md border border-slate-200 bg-white">
                    <table className="w-full text-left text-sm" data-testid="conf-tabla-roles">
                      <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-2">Rol</th>
                          <th className="px-3 py-2 text-center">Usuarios</th>
                          <th className="px-3 py-2 text-center">Con 2FA</th>
                          <th className="px-3 py-2 text-center">Pide código al entrar</th>
                          <th className="px-3 py-2 text-center">Se activa solo al crear</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(config.roles || []).map((r) => (
                          <tr key={r.id} data-testid={`conf-rol-${r.id}`}
                            className={r.bloqueado ? "bg-amber-50/60" : (r.reta_login ? "bg-emerald-50/40" : "")}>
                            <td className="px-3 py-2">
                              <p className="font-medium text-slate-900">{r.label}</p>
                              <p className="font-mono text-[10px] text-slate-400">{r.id}</p>
                              {r.bloqueado && (
                                <p data-testid={`conf-rol-${r.id}-bloqueado`}
                                  className="mt-0.5 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                                  <Lock className="h-2.5 w-2.5" /> 2FA obligatorio · no configurable
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center text-slate-600"
                              data-testid={`conf-rol-${r.id}-usuarios`}>{r.usuarios}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={r.con_2fa ? "font-semibold text-emerald-700" : "text-slate-400"}
                                data-testid={`conf-rol-${r.id}-con2fa`}>{r.con_2fa}</span>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Switch data-testid={`conf-reta-${r.id}`} checked={!!r.reta_login}
                                disabled={busy === `reta-${r.id}` || !!r.bloqueado}
                                title={r.motivo_bloqueo || ""}
                                onCheckedChange={(v) => toggleReta(r.id, r.label, v)} />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Switch data-testid={`conf-auto-${r.id}`} checked={!!r.auto_activar}
                                disabled={busy === `auto-${r.id}` || !!r.bloqueado}
                                title={r.motivo_bloqueo || ""}
                                onCheckedChange={(v) => toggleAuto(r.id, v)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mb-3 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input data-testid="panel-2fa-buscar" value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar por nombre, correo, entidad o rol…" className="pl-8" />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Checkbox data-testid="panel-2fa-filtro-exigibles" checked={soloExigibles}
                    onCheckedChange={(v) => setSoloExigibles(!!v)} />
                  Solo roles con 2FA
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Checkbox data-testid="panel-2fa-filtro-apagados" checked={soloApagados}
                    onCheckedChange={(v) => setSoloApagados(!!v)} />
                  Solo sin 2FA
                </label>
              </div>

              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Button data-testid="panel-2fa-activar-lote" size="sm"
                  disabled={!seleccionados.length || busy === "lote"}
                  onClick={activarLote}
                  className="bg-primary hover:bg-sidebar-hover">
                  {busy === "lote" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    : <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />}
                  Activar 2FA a seleccionados ({seleccionados.length})
                </Button>
                <TooltipButton
                  tooltip="Activa el 2FA a todos los usuarios de los roles que tienen encendido «se activa solo al crear». Muestra primero la lista completa para confirmar."
                  data-testid="panel-2fa-activar-todos" size="sm" variant="outline"
                  disabled={busy === "previa"} onClick={abrirMasivo}>
                  {busy === "previa" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    : <Users className="mr-1.5 h-3.5 w-3.5" />}
                  Activar 2FA a todos los roles con activación automática
                </TooltipButton>
                {!!seleccionablesVisibles.length && (
                  <button type="button" data-testid="panel-2fa-sel-todos"
                    onClick={() => toggleTodosVisibles(true)}
                    className="text-xs font-medium text-accent hover:underline">
                    Seleccionar los {seleccionablesVisibles.length} visibles sin 2FA
                  </button>
                )}
                {!!seleccionados.length && (
                  <button type="button" data-testid="panel-2fa-sel-limpiar"
                    onClick={() => setSel({})}
                    className="text-xs font-medium text-slate-500 hover:underline">
                    Limpiar selección
                  </button>
                )}
              </div>

              <div className="max-h-[520px] overflow-auto rounded-md border border-slate-200">
                <table className="w-full text-left text-sm" data-testid="panel-2fa-tabla">
                  <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="w-10 px-3 py-2" />
                      <th className="px-3 py-2">Nombre</th>
                      <th className="px-3 py-2">Correo</th>
                      <th className="px-3 py-2">Entidad</th>
                      <th className="px-3 py-2">Rol</th>
                      <th className="px-3 py-2">2FA</th>
                      <th className="px-3 py-2">Último acceso</th>
                      <th className="px-3 py-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filas.map((row) => (
                      <tr key={row.id} data-testid={`panel-2fa-fila-${row.id}`}
                        className={row.mfa_exigible_por_rol ? "" : "bg-slate-50/50"}>
                        <td className="px-3 py-2">
                          {row.mfa_exigible_por_rol && !row.mfa_activo && (
                            <Checkbox data-testid={`panel-2fa-check-${row.id}`}
                              checked={!!sel[row.id]}
                              onCheckedChange={(v) => setSel((s) => ({ ...s, [row.id]: !!v }))} />
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-900">{row.name || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{row.email}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{row.entidad}</td>
                        <td className="px-3 py-2">
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
                            {row.role}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <Badge2FA on={row.mfa_activo} exigible={row.mfa_exigible_por_rol} id={row.id} />
                          {row.mfa_activo && !row.proximo_login_pide_codigo && (
                            <span className="ml-1 text-[10px] text-slate-500"
                              data-testid={`panel-2fa-primer-login-${row.id}`}>
                              (1er login sin código)
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{fecha(row.ultimo_acceso)}</td>
                        <td className="px-3 py-2">
                          {row.mfa_exigible_por_rol ? (
                            <div className="flex items-center gap-1">
                              <TooltipButton
                                tooltip={row.mfa_activo ? "Apagar el 2FA de este usuario" : "Encender el 2FA de este usuario"}
                                data-testid={`panel-2fa-toggle-${row.id}`} size="sm" variant="ghost"
                                className="h-7 px-2 text-xs" disabled={busy === row.id}
                                onClick={() => toggleUno(row)}>
                                {busy === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : row.mfa_activo ? <ShieldOff className="h-3.5 w-3.5 text-slate-500" />
                                    : <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}
                              </TooltipButton>
                              <TooltipButton
                                tooltip="Reset de rescate: apaga el 2FA, invalida códigos vigentes y limpia el bloqueo por intentos"
                                data-testid={`panel-2fa-reset-${row.id}`} size="sm" variant="ghost"
                                className="h-7 px-2 text-xs" onClick={() => setReset(row)}>
                                <KeyRound className="h-3.5 w-3.5 text-amber-600" />
                              </TooltipButton>
                            </div>
                          ) : <span className="text-xs text-slate-300">—</span>}
                        </td>
                      </tr>
                    ))}
                    {!filas.length && (
                      <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-400"
                        data-testid="panel-2fa-vacio">Ningún usuario coincide con el filtro.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Mostrando <b data-testid="panel-2fa-conteo-filas">{filas.length}</b> de {(data.items || []).length} usuarios.
              </p>
            </>
          )}
        </div>
      )}

      {/* Confirmación de APAGADO del interruptor general */}
      <AlertDialog open={confGate} onOpenChange={(v) => !v && setConfGate(false)}>
        <AlertDialogContent data-testid="conf-gate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Power className="h-4 w-4 text-red-600" /> Apagar el 2FA de toda la plataforma
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  <b data-testid="conf-gate-impacto">{data?.resumen?.con_2fa_activo ?? 0}</b> usuario(s)
                  con 2FA activo dejarán de recibir el código y entrarán solo con correo y contraseña.
                </p>
                <p className="text-xs">
                  No se borra nada: la bandera <code>mfa_activo</code> de cada usuario y los
                  interruptores por rol quedan intactos. Al volver a encender, todo retoma su
                  estado sin redesplegar.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conf-gate-cancelar" disabled={busy === "gate"}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="conf-gate-confirmar" disabled={busy === "gate"}
              onClick={(e) => { e.preventDefault(); confirmarApagadoGate(); }}
              className="bg-red-600 hover:bg-red-700">
              {busy === "gate" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Apagar el 2FA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Apagar «pide código» de un rol → advertencia con conteo.
          Encenderlo → oferta de activarlo a los usuarios existentes, con la lista. */}
      <AlertDialog open={!!confRol} onOpenChange={(v) => !v && setConfRol(null)}>
        <AlertDialogContent className="max-w-2xl" data-testid="conf-rol-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confRol?.accion === "apagar"
                ? <><AlertTriangle className="h-4 w-4 text-amber-600" /> Apagar el 2FA del rol {confRol?.label}</>
                : <><ShieldCheck className="h-4 w-4 text-emerald-600" /> Activar el 2FA a los {confRol?.impacto?.sin_2fa} usuario(s) de {confRol?.label}</>}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {confRol?.accion === "apagar" ? (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900"
                    data-testid="conf-rol-advertencia">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <span>{confRol?.impacto?.advertencia_al_apagar}</span>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600" data-testid="conf-rol-oferta">
                    {confRol?.impacto?.oferta_al_encender} Revisa la lista antes de aplicar.
                  </p>
                )}
                <div className="max-h-64 overflow-auto rounded-md border border-slate-200">
                  <table className="w-full text-left text-xs" data-testid="conf-rol-lista">
                    <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="px-2 py-1.5">#</th>
                        <th className="px-2 py-1.5">Nombre</th>
                        <th className="px-2 py-1.5">Correo</th>
                        <th className="px-2 py-1.5">Entidad</th>
                        <th className="px-2 py-1.5">Rol</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {((confRol?.accion === "apagar"
                        ? confRol?.impacto?.items_con_2fa
                        : confRol?.impacto?.items_sin_2fa) || []).map((it, i) => (
                        <tr key={it.id} data-testid={`conf-rol-fila-${it.id}`}>
                          <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                          <td className="px-2 py-1.5 font-medium text-slate-800">{it.name || "—"}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-600">{it.email}</td>
                          <td className="px-2 py-1.5 text-slate-600">{it.entidad}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-500">{it.role}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conf-rol-cancelar" disabled={busy === "rol"}>
              {confRol?.accion === "apagar" ? "Cancelar" : "Ahora no"}
            </AlertDialogCancel>
            <AlertDialogAction data-testid="conf-rol-confirmar" disabled={busy === "rol"}
              onClick={(e) => {
                e.preventDefault();
                if (confRol?.accion === "apagar") confirmarApagadoRol(); else confirmarActivarRol();
              }}
              className={confRol?.accion === "apagar"
                ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}>
              {busy === "rol" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              {confRol?.accion === "apagar"
                ? `Apagar de todos modos (${confRol?.impacto?.con_2fa ?? 0} afectados)`
                : `Activar a los ${confRol?.impacto?.sin_2fa ?? 0}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Vista previa OBLIGATORIA de la activación masiva: la lista completa con nombre,
          correo, entidad y rol antes de aplicar nada. */}
      <AlertDialog open={!!masivo} onOpenChange={(v) => !v && setMasivo(null)}>
        <AlertDialogContent className="max-w-3xl" data-testid="masivo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Activar 2FA a {masivo?.afectados ?? 0} usuario(s)
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Roles objetivo: <b className="font-mono">{(masivo?.roles_objetivo || []).join(", ")}</b>.
                  {" "}Ya tenían 2FA: <b>{masivo?.ya_tenian_2fa ?? 0}</b>.
                </p>
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <span>{masivo?.advertencia}</span>
                </div>
                <div className="max-h-72 overflow-auto rounded-md border border-slate-200">
                  <table className="w-full text-left text-xs" data-testid="masivo-lista">
                    <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="px-2 py-1.5">#</th>
                        <th className="px-2 py-1.5">Nombre</th>
                        <th className="px-2 py-1.5">Correo</th>
                        <th className="px-2 py-1.5">Entidad</th>
                        <th className="px-2 py-1.5">Rol</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(masivo?.items || []).map((it, i) => (
                        <tr key={it.id} data-testid={`masivo-fila-${it.id}`}>
                          <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                          <td className="px-2 py-1.5 font-medium text-slate-800">{it.name || "—"}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-600">{it.email}</td>
                          <td className="px-2 py-1.5 text-slate-600">{it.entidad}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-500">{it.role}</td>
                        </tr>
                      ))}
                      {!(masivo?.items || []).length && (
                        <tr><td colSpan={5} className="px-2 py-6 text-center text-slate-400">
                          Todos los usuarios objetivo ya tienen el 2FA activo.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="masivo-cancelar" disabled={busy === "masivo"}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="masivo-confirmar"
              disabled={busy === "masivo" || !(masivo?.afectados)}
              onClick={(e) => { e.preventDefault(); confirmarMasivo(); }}
              className="bg-primary hover:bg-sidebar-hover">
              {busy === "masivo" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Confirmar y activar {masivo?.afectados ?? 0}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset individual (paracaídas ya existente, conservado) */}
      <AlertDialog open={!!reset} onOpenChange={(v) => !v && setReset(null)}>
        <AlertDialogContent data-testid="reset-2fa-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-amber-600" /> Resetear 2FA (rescate)
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>Usuario: <b>{reset?.name}</b> <span className="font-mono text-slate-400">({reset?.email})</span></p>
                <p>
                  Se apaga su 2FA, se invalidan los códigos vigentes y se limpia el bloqueo por
                  intentos fallidos. El usuario vuelve a entrar con solo correo y contraseña.
                  Queda en bitácora como <code>mfa_reset_super_admin</code>.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-2fa-cancelar" disabled={busy === "reset"}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="reset-2fa-confirmar" disabled={busy === "reset"}
              onClick={(e) => { e.preventDefault(); confirmarReset(); }}
              className="bg-amber-600 hover:bg-amber-700">
              {busy === "reset" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Resetear 2FA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
